/**
 * Single-item AI proposal review — the confirmation gate for the modular
 * items' bi-directional sync. Given an `original` value and a `generate`
 * thunk (one of the proposeItem* helpers), it runs the AI pass, shows the
 * result as a red/green diff (reusing line-diff, like ReconcileDialog), and
 * applies it only when the designer confirms. Nothing is written until Apply.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { diffLines, type DiffRowType } from './lib/line-diff';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const DIFF_ROW_CLASS: Record<DiffRowType, string> = {
  add: 'bg-success/12 text-success',
  del: 'bg-error/12 text-error',
  context: 'text-crucible-muted',
};
const DIFF_ROW_SIGN: Record<DiffRowType, string> = { add: '+', del: '-', context: ' ' };

export interface ItemProposalDialogProps {
  open: boolean;
  title: string;
  /** Verb shown on the apply button, e.g. "Update details" / "Use description". */
  applyLabel: string;
  /** Optional context line shown above the diff (e.g. explaining a freeform fallback). */
  note?: string;
  /** When set, the dialog first asks the designer for a freeform instruction
   *  (a textarea) and passes it to `generate`. Used by the per-item Revise. */
  instruction?: { prompt: string; placeholder?: string; submitLabel?: string };
  original: string;
  /** Produce the proposal asynchronously (e.g. an AI pass). Receives the
   *  designer's instruction when the `instruction` stage is enabled. */
  generate?: (instruction: string) => Promise<string>;
  /** Or supply an already-computed proposal (skips the working stage). */
  proposed?: string;
  onApply: (proposed: string) => void;
  onOpenChange: (open: boolean) => void;
}

type Stage = 'instruction' | 'working' | 'review' | 'error';

export function ItemProposalDialog({
  open,
  title,
  applyLabel,
  note,
  instruction,
  original,
  generate,
  proposed: proposedProp,
  onApply,
  onOpenChange,
}: ItemProposalDialogProps): ReactElement {
  const [stage, setStage] = useState<Stage>('working');
  const [proposed, setProposed] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [instructionText, setInstructionText] = useState('');
  const runRef = useRef(0);

  const runGenerate = (text: string): void => {
    const myRun = (runRef.current += 1);
    const live = (): boolean => runRef.current === myRun;
    setStage('working');
    setProposed('');
    setError(null);
    generate?.(text)
      .then((result) => {
        if (!live()) return;
        setProposed(result);
        setStage('review');
      })
      .catch((e: unknown) => {
        if (!live()) return;
        setError(e instanceof Error ? e.message : String(e));
        setStage('error');
      });
  };

  useEffect(() => {
    if (!open) {
      runRef.current += 1;
      return;
    }
    const myRun = (runRef.current += 1);
    setInstructionText('');
    // A pre-computed proposal needs no async pass — go straight to review.
    if (proposedProp !== undefined) {
      setError(null);
      setProposed(proposedProp);
      setStage('review');
      return;
    }
    // Revise: collect the designer's instruction before generating anything.
    if (instruction) {
      setError(null);
      setProposed('');
      setStage('instruction');
      return;
    }
    const live = (): boolean => runRef.current === myRun;
    setStage('working');
    setProposed('');
    setError(null);
    generate?.('')
      .then((text) => {
        if (!live()) return;
        setProposed(text);
        setStage('review');
      })
      .catch((e: unknown) => {
        if (!live()) return;
        setError(e instanceof Error ? e.message : String(e));
        setStage('error');
      });
    // `generate`/`proposed` are recreated per-open by the caller; key the effect
    // on `open` so we run exactly once when the dialog opens.
  }, [open]);

  const unchanged = stage === 'review' && proposed.trim() === original.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {stage === 'instruction' && instruction && (
          <div data-testid="item-proposal-instruction">
            <p className="mb-2 text-sm text-crucible-muted">{instruction.prompt}</p>
            <textarea
              data-testid="item-proposal-instruction-input"
              value={instructionText}
              onChange={(e) => setInstructionText(e.currentTarget.value)}
              rows={3}
              placeholder={instruction.placeholder ?? 'e.g. make it harder and add a time limit'}
              className="w-full rounded border border-crucible-border bg-crucible-bg p-2 text-sm text-crucible-fg outline-none focus:border-crucible-accent"
            />
            <DialogFooter>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                data-testid="item-proposal-instruction-submit"
                disabled={!instructionText.trim()}
                onClick={() => runGenerate(instructionText.trim())}
              >
                {instruction.submitLabel ?? 'Revise'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'working' && (
          <div
            className="py-6 text-center text-sm text-crucible-muted"
            data-testid="item-proposal-working"
          >
            Asking the Co-Designer…
          </div>
        )}

        {stage === 'error' && (
          <div data-testid="item-proposal-error">
            <p className="mb-3 rounded border border-error/25 bg-error/12 p-2 text-xs text-error">
              {error}
            </p>
            <DialogFooter>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'review' && (
          <div data-testid="item-proposal-review">
            {note && (
              <p
                className="mb-3 rounded border border-crucible-border bg-crucible-bg-elevated p-2 text-xs text-crucible-muted"
                data-testid="item-proposal-note"
              >
                {note}
              </p>
            )}
            {unchanged ? (
              <p className="mb-4 text-sm text-crucible-muted" data-testid="item-proposal-unchanged">
                The Co-Designer didn't suggest any change.
              </p>
            ) : (
              <div className="mb-4 rounded bg-crucible-bg-elevated p-2">
                <div className="max-h-72 overflow-y-auto rounded font-mono f-accent leading-snug">
                  {diffLines(original, proposed).map((row, idx) => (
                    <div key={idx} className={`flex gap-1.5 px-1 ${DIFF_ROW_CLASS[row.type]}`}>
                      <span aria-hidden className="select-none opacity-50">
                        {DIFF_ROW_SIGN[row.type]}
                      </span>
                      <span className="min-w-0 whitespace-pre-wrap break-words">
                        {row.text || ' '}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                data-testid="item-proposal-apply"
                disabled={unchanged}
                onClick={() => {
                  onApply(proposed);
                  onOpenChange(false);
                }}
              >
                {applyLabel}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
